import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import Editor, { type OnMount } from '@monaco-editor/react';
import { ArrowLeft, Check, Copy, Loader2, Play } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/components/ui/button';
import { useAuthToken } from '@/hooks/useAuthToken';
import { usePermissions } from '@/hooks/usePermissions';
import { useResponsive } from '@/hooks/useResponsive';
import { builderApi } from '@/lib/api/builder';
import { getTemplateIdForTags } from '@/lib/templates';
import { cn } from '@/lib/utils';
import {
  extractFinalPrompt,
  extractGuideContent,
  getPromptById,
} from '@/lib/prompt-lib-utils';

export default function PromptDetailPage() {
  const { promptId } = useParams<{ promptId: string }>();
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { canCreateProject } = usePermissions();
  const { isMobile } = useResponsive();

  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

  const prompt = useMemo(() => {
    if (!promptId) return null;
    return getPromptById(promptId);
  }, [promptId]);

  const guideContent = useMemo(() => {
    if (!prompt) return '';
    return extractGuideContent(prompt.content);
  }, [prompt]);

  const [finalPrompt, setFinalPrompt] = useState(() => {
    if (!prompt) return '';
    return extractFinalPrompt(prompt.content);
  });

  // Scroll to top when page mounts
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready and scroll happens after any browser scroll restoration
    requestAnimationFrame(() => {
      const mainContainer = document.querySelector('main');
      if (mainContainer) {
        mainContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    });
  }, [promptId]);

  // Handle wheel events on editor to allow page scroll at boundaries
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      const editor = editorRef.current;
      if (!editor) return;

      const scrollTop = editor.getScrollTop();
      const scrollHeight = editor.getScrollHeight();
      const containerHeight = editor.getLayoutInfo().height;
      const maxScroll = scrollHeight - containerHeight;

      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop >= maxScroll - 1;
      const isScrollingUp = e.deltaY < 0;
      const isScrollingDown = e.deltaY > 0;

      // At boundaries, prevent Monaco from handling and let page scroll
      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        e.stopPropagation();
        // Manually scroll the main container
        const mainContainer = document.querySelector('main');
        if (mainContainer) {
          mainContainer.scrollBy({ top: e.deltaY, behavior: 'auto' });
        }
      }
    };

    // Use capture phase to intercept before Monaco handles it
    container.addEventListener('wheel', handleWheel, { capture: true });
    return () => container.removeEventListener('wheel', handleWheel, { capture: true });
  }, []);

  // Update finalPrompt when prompt changes
  useMemo(() => {
    if (prompt) {
      setFinalPrompt(extractFinalPrompt(prompt.content));
    }
  }, [prompt]);

  const handleCopyPrompt = async () => {
    if (!finalPrompt.trim()) return;

    try {
      await navigator.clipboard.writeText(finalPrompt);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRunPrompt = async () => {
    if (!token || !finalPrompt.trim()) return;

    setIsRunning(true);
    setError(null);

    try {
      // 1. Pre-flight classification
      const preFlight = await builderApi.preFlight(token, {
        prompt: finalPrompt,
        uploadedFiles: [],
      });

      // 2. Create project + dev-container
      const result = await builderApi.complete(token, {
        slug: preFlight.slug,
        templateId: getTemplateIdForTags(preFlight.tags),
        builderData: {
          fromBuilder: true,
          initialPrompt: finalPrompt,
          appType: preFlight.app_type,
          tags: preFlight.tags,
          mode: 'execute',
          referenceUrls: [],
          uploadedFiles: [],
        },
      });

      // 3. Redirect to Power Editor
      navigate(result.redirect);
    } catch (err) {
      console.error('Failed to run prompt:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to create project'
      );
      setIsRunning(false);
    }
  };

  if (!prompt) {
    return (
      <div className="min-h-full bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Prompt not found</p>
          <Link
            to="/dashboard/prompt-library"
            className="text-primary hover:text-primary/80"
          >
            Back to Prompt Library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background text-foreground">
      <div className={cn(
        "max-w-[1400px] mx-auto",
        isMobile ? "px-4 py-6" : "px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10"
      )}>
        {/* Breadcrumb + Title */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link
              to="/dashboard/prompt-library"
              className="hover:text-foreground transition-colors"
            >
              Prompt Library
            </Link>
            <span>/</span>
            <span className="text-foreground/70 truncate">{prompt.title}</span>
          </div>
          <h1 className={cn(
            "font-semibold tracking-tight",
            isMobile ? "text-xl" : "text-xl sm:text-2xl"
          )}>{prompt.title}</h1>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Guide */}
          <div className="flex-1 min-w-0 lg:sticky lg:top-6" style={{ maxHeight: "calc(100vh - 160px)" }}>
            <div className="rounded-xl bg-white dark:bg-card border border-border/50 p-5 sm:p-6 h-full overflow-y-auto">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 px-1">Guide</h2>
              <div className={cn(
                "prose dark:prose-invert max-w-none prose-sm",
                "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-foreground",
                "[&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-foreground/90",
                "[&_p]:text-sm [&_p]:text-muted-foreground [&_p]:mb-3 [&_p]:leading-relaxed",
                "[&_ul]:text-sm [&_ul]:text-muted-foreground [&_ul]:mb-3 [&_ul]:space-y-1",
                "[&_li]:text-sm [&_li]:text-muted-foreground",
                "[&_strong]:text-foreground [&_strong]:font-semibold",
                "[&_img]:rounded-lg [&_img]:my-4 [&_img]:border [&_img]:border-border/50",
                "[&_a]:text-primary [&_a]:no-underline hover:[&_a]:underline",
                "[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground [&_blockquote]:italic",
              )}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {guideContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {/* Right: Final Prompt */}
          <div className="w-full lg:w-[420px] shrink-0 lg:sticky lg:top-6" style={{ maxHeight: "calc(100vh - 160px)" }}>
              <div className="rounded-xl bg-white dark:bg-card border border-border/50 overflow-hidden h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Final Prompt
                  </h2>
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleCopyPrompt}
                      disabled={!finalPrompt.trim()}
                      className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md border border-border text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                    >
                      {isCopied ? (
                        <><Check className="h-3.5 w-3.5 mr-1" />Copied</>
                      ) : (
                        <><Copy className="h-3.5 w-3.5 mr-1" />Copy</>
                      )}
                    </button>
                    <button
                      onClick={handleRunPrompt}
                      disabled={isRunning || !finalPrompt.trim() || !canCreateProject}
                      title={!canCreateProject ? "You don't have permission to create projects" : undefined}
                      className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md bg-foreground/90 text-background hover:bg-foreground/80 transition-colors disabled:opacity-50"
                    >
                      {isRunning ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Creating...</>
                      ) : (
                        <><Play className="h-3.5 w-3.5 mr-1" />Run</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Monaco Editor */}
                <div ref={editorContainerRef} className="flex-1">
                  <Editor
                    height="100%"
                    defaultLanguage="markdown"
                    value={finalPrompt}
                    onChange={(value) => setFinalPrompt(value || '')}
                    onMount={(editor) => {
                      editorRef.current = editor;
                    }}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'off',
                      wordWrap: 'on',
                      padding: { top: 12, bottom: 12 },
                      scrollBeyondLastLine: false,
                    }}
                  />
                </div>
              </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className={cn(
            "bg-destructive/10 border border-destructive/50 rounded-lg text-destructive",
            isMobile ? "mb-3 p-3 text-sm" : "mb-4 p-4"
          )}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
